/**
 * 前端 API 层 — 与后端 Go server 通信
 *
 * 前端只发送简洁的业务请求，所有网关细节由 server 封装处理。
 */

import type {
  ActivityCheckResult,
  ApiRequestError,
  EnhancePromptResponse,
  GenerateRequestOptions,
  GenerateResponse,
  QuotaInfo,
  TaskPollResponse,
} from '../types'
import { trackedFetch } from '../util/apiReport'

const API_BASE = 'https://docer-api.wps.cn/ai-docer/aivideo/api'

let _reqCounter = 0
function genRequestId(): string {
  _reqCounter++
  const ts = Date.now().toString(36)
  const rnd = Math.random().toString(36).substring(2, 6)
  return `${ts}-${rnd}-${_reqCounter}`
}

function withRequestIdUrl(rawUrl: string): string {
  const url = new URL(rawUrl)
  url.searchParams.set('x-request-id', genRequestId())
  return url.toString()
}

interface GenerateRequestBody {
  prompt: string
  duration: string
  size: string
  camera_fixed: boolean
  model: string
  input_reference?: string
}

export async function callGenerateAPI(
  prompt: string,
  params: { duration: string },
  imageBase64: string | null,
  {
    selectedCamera,
    selectedStyle,
    selectedModel,
    cameraPrefixes,
    styleSuffixes,
    cameraFixed,
  }: GenerateRequestOptions
): Promise<GenerateResponse> {
  const enrichedPrompt =
    (cameraPrefixes[selectedCamera] || '') + prompt + (styleSuffixes[selectedStyle] || '')
  const dur = params.duration.replace('s', '')

  const reqBody: GenerateRequestBody = {
    prompt: enrichedPrompt,
    duration: dur,
    size: '480p',
    camera_fixed: !!cameraFixed[selectedCamera],
    model: (selectedModel ?? '') || '',
  }

  if (imageBase64) {
    reqBody.input_reference = imageBase64
  }

  console.log('[API] 发送生成请求:', reqBody.prompt.substring(0, 80) + '...')

  const res = await trackedFetch(withRequestIdUrl(`${API_BASE}/generate`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reqBody),
  })

  if (!res.ok) {
    let errMsg = `API ${res.status}`
    try {
      const errBody = (await res.json()) as { error?: string; message?: string; error_code?: string }
      errMsg = errBody.error || errBody.message || errMsg
      const err = new Error(errMsg) as ApiRequestError
      err.serverError = errMsg
      err.errorCode = errBody.error_code || ''
      throw err
    } catch (e) {
      if (e && typeof e === 'object' && 'serverError' in e) throw e
      throw new Error(errMsg)
    }
  }
  return (await res.json()) as GenerateResponse
}

export async function pollTaskAPI(taskId: string): Promise<TaskPollResponse> {
  const res = await trackedFetch(withRequestIdUrl(`${API_BASE}/task/${taskId}`), {
    credentials: 'include',
  })
  if (!res.ok) {
    try {
      const errBody = (await res.json()) as { error?: string }
      if (errBody.error) {
        return { status: 'failed', error: errBody.error, task_id: taskId }
      }
    } catch {
      /* not json */
    }
    throw new Error(`Poll HTTP ${res.status}`)
  }
  return (await res.json()) as TaskPollResponse
}

export function extractVideoUrl(data: TaskPollResponse): string | null {
  if (data.video_url) return data.video_url
  if (data.content && Array.isArray(data.content)) {
    for (const item of data.content) {
      if (item.type === 'video_url') {
        const vu = item.video_url
        if (vu && typeof vu === 'object' && 'url' in vu) return vu.url || item.url || null
        if (typeof vu === 'string') return vu
        return item.url || null
      }
      if (item.video_url) {
        const vu = item.video_url
        if (vu && typeof vu === 'object' && 'url' in vu) return vu.url || null
        if (typeof vu === 'string') return vu
      }
    }
  }
  if (data.output?.video_url) return data.output.video_url
  if (data.result?.video_url) return data.result.video_url
  return null
}

const UPLOAD_TIMEOUT_MS = 30_000

export async function uploadImage(file: File | Blob, filename = 'image.jpg'): Promise<string> {
  const doUpload = async (): Promise<string> => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS)
    try {
      const form = new FormData()
      form.append('file', file, filename)
      const res = await trackedFetch(withRequestIdUrl(`${API_BASE}/image/upload`), {
        method: 'POST',
        body: form,
        credentials: 'include',
        signal: controller.signal,
      })
      if (!res.ok) {
        let msg = `upload HTTP ${res.status}`
        try {
          const body = (await res.json()) as { error?: string }
          if (body.error) msg = body.error
        } catch { /* ignore */ }
        throw new Error(msg)
      }
      const body = (await res.json()) as { url?: string }
      if (!body.url) throw new Error('upload response missing url')
      return body.url
    } finally {
      clearTimeout(timer)
    }
  }

  try {
    return await doUpload()
  } catch (first) {
    try {
      return await doUpload()
    } catch {
      throw first
    }
  }
}

export async function enhancePromptAPI(
  prompt: string,
  onChunk?: (text: string) => void
): Promise<EnhancePromptResponse> {
  const res = await trackedFetch(withRequestIdUrl(`${API_BASE}/enhance-prompt`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  })
  if (!res.ok) throw new Error('增强接口暂未开通')

  const reader = res.body?.getReader()
  if (!reader) throw new Error('浏览器不支持流式读取')

  const decoder = new TextDecoder()
  let buffer = ''
  let fullText = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    let eventType = ''
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim()
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6)
        if (eventType === 'chunk') {
          try {
            const parsed = JSON.parse(data)
            if (parsed.text) {
              fullText += parsed.text
              onChunk?.(parsed.text)
            }
          } catch { /* ignore */ }
        } else if (eventType === 'error') {
          throw new Error(data)
        } else if (eventType === 'done') {
          return { enhanced: fullText }
        }
        eventType = ''
      }
    }
  }

  if (fullText) return { enhanced: fullText }
  throw new Error('未收到有效响应')
}

export async function fetchQuota(): Promise<QuotaInfo> {
  const res = await trackedFetch(withRequestIdUrl(`${API_BASE}/quota`), {
    credentials: 'include',
  })
  if (!res.ok) {
    let errMsg = `HTTP ${res.status}`
    try {
      const body = (await res.json()) as { error?: string; message?: string }
      errMsg = body.message || body.error || errMsg
    } catch { /* ignore */ }
    throw new Error(errMsg)
  }
  return (await res.json()) as QuotaInfo
}

export async function checkActivity(): Promise<ActivityCheckResult> {
  const res = await trackedFetch(withRequestIdUrl(`${API_BASE}/activity/check`), {
    credentials: 'include',
  })
  if (!res.ok) {
    let errMsg = `HTTP ${res.status}`
    try {
      const body = (await res.json()) as { error?: string; message?: string }
      errMsg = body.message || body.error || errMsg
    } catch { /* ignore */ }
    throw new Error(errMsg)
  }
  return (await res.json()) as ActivityCheckResult
}

